// pages/UserManagement.js - v3.0 User Management Dashboard

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Form, Modal, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import DesktopLayout from '../layouts/DesktopLayout';
import axios from 'axios';
import { roleConfig, isOwnerRole } from '../config/roleConfig';
import PhoneInput from '../components/PhoneInput';
import { getRoleDisplayName } from '../config/navigation';
import { formatDate as sharedFormatDate, formatDateTime as sharedFormatDateTime } from '../utils/dateFormatter';

// Roles manageable via this page — exclude system/API roles not managed here
const USER_MANAGEABLE_ROLES = Object.keys(roleConfig).filter(r => !['serviceClient', 'service'].includes(r));

const UserManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { setSelectedAccount } = useAccount();
  const [activeNavItem, setActiveNavItem] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    role: '',
    search: ''
  });

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Edit form
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    role: ''
  });

  // Invite form
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    emailConfirmation: '',
    phone: '',
    role: 'turnKeyOwner'
  });

  // Suspend form
  const [suspendReason, setSuspendReason] = useState('');

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.role) params.append('role', filters.role);
      if (filters.search) params.append('search', filters.search);
      params.append('includePropertyCount', 'true'); // Include property counts for owners

      const fullUrl = `${process.env.REACT_APP_API_BASE_URL}/api/v1/users?${params}`;

      const response = await axios.get(fullUrl, {
        headers: { 'x-auth-token': token }
      });

      setUsers(response.data.users || []);
      setError(null);
    } catch (err) {
      console.error('❌ Fetch users error:', err.message);
      setError(err.response?.data?.message || 'فشل جلب المستخدمين');
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'ops') {
      fetchUsers();
    }
  }, [user, filters, fetchUsers]);

  // Invite user
  const handleInviteUser = async (e) => {
    e.preventDefault();

    // Validate email confirmation
    if (inviteForm.email !== inviteForm.emailConfirmation) {
      setError('البريد الإلكتروني غير متطابق');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Don't send emailConfirmation to backend
      const { emailConfirmation, ...userData } = inviteForm;

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users`,
        userData,
        { headers: { 'x-auth-token': token } }
      );

      setSuccess('تم إرسال دعوة المستخدم بنجاح');
      setShowInviteModal(false);
      setInviteForm({ name: '', email: '', emailConfirmation: '', phone: '', role: 'turnKeyOwner' });
      fetchUsers();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل إرسال الدعوة');
    }
  };

  // Resend activation
  const handleResendActivation = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/${userId}/resend-activation`,
        {},
        { headers: { 'x-auth-token': token } }
      );

      setSuccess('تم إعادة إرسال رابط التفعيل بنجاح');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل إعادة إرسال التفعيل');
    }
  };

  // Suspend user
  const handleSuspendUser = async () => {
    if (!selectedUser || !suspendReason.trim()) {
      setError('سبب الإيقاف مطلوب');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/${selectedUser._id}/suspend`,
        { reason: suspendReason },
        { headers: { 'x-auth-token': token } }
      );

      setSuccess('تم إيقاف المستخدم بنجاح');
      setShowSuspendModal(false);
      setSuspendReason('');
      setSelectedUser(null);
      fetchUsers();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل إيقاف المستخدم');
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/${selectedUser._id}`,
        { headers: { 'x-auth-token': token } }
      );

      setSuccess('تم حذف المستخدم بنجاح');
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل حذف المستخدم');
      setShowDeleteModal(false);
    }
  };

  // Reactivate user
  const handleReactivateUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/${userId}/reactivate`,
        {},
        { headers: { 'x-auth-token': token } }
      );

      setSuccess('تم إعادة تفعيل المستخدم بنجاح');
      fetchUsers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل إعادة التفعيل');
    }
  };

  // Update user
  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/users/${selectedUser._id}`,
        editForm,
        { headers: { 'x-auth-token': token } }
      );

      setSuccess('تم تحديث المستخدم بنجاح');
      setIsEditMode(false);
      fetchUsers();

      // Update selected user with new data
      setSelectedUser({ ...selectedUser, ...editForm });

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'فشل تحديث المستخدم');
    }
  };

  // Handle edit mode
  const handleEnterEditMode = () => {
    if (selectedUser) {
      setEditForm({
        name: selectedUser.name,
        phone: selectedUser.phone || '',
        role: selectedUser.role
      });
      setIsEditMode(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm({ name: '', phone: '', role: '' });
  };

  // Check if invitation is expired or expiring soon
  const getInvitationStatus = (user) => {
    if (user.status !== 'invited' || !user.activationExpires) return null;

    const now = new Date();
    const expires = new Date(user.activationExpires);
    const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return { type: 'expired', daysLeft: 0 };
    } else if (daysLeft <= 7) {
      return { type: 'expiring', daysLeft };
    }
    return { type: 'valid', daysLeft };
  };

  // Get status badge with expiration indicator
  const getStatusBadge = (user) => {
    const invitationStatus = getInvitationStatus(user);

    const badges = {
      invited: (
        <>
          <Badge bg="info">مدعو</Badge>
          {invitationStatus?.type === 'expired' && (
            <Badge bg="danger" className="ms-1" title="انتهت صلاحية الدعوة">
              منتهية
            </Badge>
          )}
          {invitationStatus?.type === 'expiring' && (
            <Badge bg="warning" text="dark" className="ms-1" title={`${invitationStatus.daysLeft} أيام متبقية`}>
              ⚠️ {invitationStatus.daysLeft} يوم
            </Badge>
          )}
        </>
      ),
      active: <Badge bg="success">نشط</Badge>,
      suspended: <Badge bg="danger">موقوف</Badge>,
      archived: <Badge bg="secondary">مؤرشف</Badge>
    };
    return badges[user.status] || <Badge bg="secondary">{user.status}</Badge>;
  };

  // Format date helper — delegates to the shared formatter (business timezone)
  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return '-';
    return includeTime ? sharedFormatDateTime(dateString) : sharedFormatDate(dateString);
  };

  // Get role label — derived from centralized navigation config
  const getRoleLabel = (role) => getRoleDisplayName(role);

  // Check if user is property owner

  // Handle property count click - navigate to properties page with filter
  const handleViewProperties = (userId) => {
    setSelectedAccount(userId);
    navigate('/properties');
  };

  // Check permissions
  const canManageUsers = user?.role === 'admin' || user?.role === 'ops';
  const canSuspend = user?.role === 'admin';

  if (!canManageUsers) {
    return (
      <DesktopLayout
        activeNavItem={activeNavItem}
        setActiveNavItem={setActiveNavItem}
        headerTitle="إدارة المستخدمين"
      >
        <Container className="mt-5">
          <Alert variant="danger">
            ليس لديك صلاحية للوصول إلى هذه الصفحة
          </Alert>
        </Container>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="إدارة المستخدمين"
    >
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col xs="auto" className="ms-auto">
            <Button variant="primary" onClick={() => setShowInviteModal(true)}>
              + دعوة مستخدم جديد
            </Button>
          </Col>
        </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>البحث</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="اسم، بريد إلكتروني، أو رقم جوال..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>الحالة</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">الكل</option>
                  <option value="invited">مدعو</option>
                  <option value="active">نشط</option>
                  <option value="suspended">موقوف</option>
                  <option value="archived">مؤرشف</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>الدور</Form.Label>
                <Form.Select
                  value={filters.role}
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                >
                  <option value="">الكل</option>
                  {USER_MANAGEABLE_ROLES.map(role => (
                    <option key={role} value={role}>{getRoleDisplayName(role)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Users Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <Alert variant="info">لا توجد مستخدمين</Alert>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>البريد الإلكتروني</th>
                  <th>الجوال</th>
                  <th>الدور</th>
                  <th>العقارات</th>
                  <th>الحالة</th>
                  <th>تاريخ الدعوة</th>
                  <th>آخر دخول</th>
                  <th>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id} className={getInvitationStatus(u)?.type === 'expired' ? 'table-danger' : ''}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td style={{ direction: 'ltr', textAlign: 'left' }}>{u.phone || '-'}</td>
                    <td>{getRoleLabel(u.role)}</td>
                    <td>
                      {isOwnerRole(u.role) ? (
                        u.propertyCount === null || u.propertyCount === undefined ? (
                          <span className="text-muted">-</span>
                        ) : u.propertyCount > 0 ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            onClick={() => handleViewProperties(u._id)}
                            title="عرض العقارات"
                          >
                            {u.propertyCount} {u.propertyCount === 1 ? 'عقار' : 'عقارات'}
                          </Button>
                        ) : (
                          <span className="text-muted">لا توجد عقارات</span>
                        )
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{getStatusBadge(u)}</td>
                    <td>{formatDate(u.invitedAt)}</td>
                    <td>
                      {u.lastLogin ? (
                        <span title={formatDate(u.lastLogin, true)}>
                          {formatDate(u.lastLogin)}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => {
                          setSelectedUser(u);
                          setShowDetailsModal(true);
                        }}
                      >
                        عرض
                      </Button>

                      {u.status === 'invited' && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => handleResendActivation(u._id)}
                        >
                          إعادة إرسال
                        </Button>
                      )}

                      {u.status === 'active' && canSuspend && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => {
                            setSelectedUser(u);
                            setShowSuspendModal(true);
                          }}
                        >
                          إيقاف
                        </Button>
                      )}

                      {u.status === 'suspended' && canSuspend && (
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => handleReactivateUser(u._id)}
                        >
                          إعادة تفعيل
                        </Button>
                      )}

                      {user?.role === 'admin' && u._id !== user._id && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="me-2"
                          onClick={() => {
                            setSelectedUser(u);
                            setShowDeleteModal(true);
                          }}
                        >
                          حذف
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Invite User Modal */}
      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)} dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title>دعوة مستخدم جديد</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleInviteUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>الاسم</Form.Label>
              <Form.Control
                type="text"
                required
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>البريد الإلكتروني</Form.Label>
              <Form.Control
                type="email"
                required
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>تأكيد البريد الإلكتروني</Form.Label>
              <Form.Control
                type="email"
                required
                value={inviteForm.emailConfirmation}
                onChange={(e) => setInviteForm({ ...inviteForm, emailConfirmation: e.target.value })}
                isInvalid={inviteForm.emailConfirmation && inviteForm.email !== inviteForm.emailConfirmation}
              />
              <Form.Control.Feedback type="invalid">
                البريد الإلكتروني غير متطابق
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>رقم الجوال</Form.Label>
              <PhoneInput
                value={inviteForm.phone}
                defaultCountry="SA"
                onChange={(phone) => setInviteForm({ ...inviteForm, phone })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>الدور</Form.Label>
              <Form.Select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
              >
                {user?.role === 'admin'
                  ? USER_MANAGEABLE_ROLES.map(role => (
                      <option key={role} value={role}>{getRoleDisplayName(role)}</option>
                    ))
                  : <option value="turnKeyOwner">{getRoleDisplayName('turnKeyOwner')}</option>
                }
              </Form.Select>
              {user?.role === 'admin' && (
                <Form.Text className="text-muted">
                  لإضافة عميل خدمة نظافة، استخدم صفحة <a href="#/services/clients/new">خدمات النظافة ← إضافة عميل</a>
                </Form.Text>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowInviteModal(false)}>
              إلغاء
            </Button>
            <Button variant="primary" type="submit">
              إرسال الدعوة
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* User Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setIsEditMode(false);
        }}
        size="lg"
        dir="rtl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'تعديل المستخدم' : 'تفاصيل المستخدم'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <div>
              {isEditMode ? (
                // Edit Mode
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>الاسم:</strong>
                      {selectedUser.clientCode && (
                        <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.85em' }} dir="ltr">{selectedUser.clientCode}</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </Form.Group>

                  <p><strong>البريد الإلكتروني:</strong> {selectedUser.email}</p>

                  <Form.Group className="mb-3">
                    <Form.Label><strong>الجوال:</strong></Form.Label>
                    <PhoneInput
                      value={editForm.phone}
                      defaultCountry="SA"
                      onChange={(phone) => setEditForm({ ...editForm, phone })}
                    />
                  </Form.Group>

                  {user?.role === 'admin' && (
                    <Form.Group className="mb-3">
                      <Form.Label><strong>الدور:</strong></Form.Label>
                      <Form.Select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                      >
                        {USER_MANAGEABLE_ROLES.map(role => (
                          <option key={role} value={role}>{getRoleDisplayName(role)}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}

                  {user?.role !== 'admin' && (
                    <p><strong>الدور:</strong> {getRoleLabel(selectedUser.role)}</p>
                  )}

                  <p><strong>الحالة:</strong> {getStatusBadge(selectedUser)}</p>
                  {selectedUser.suspensionReason && (
                    <p><strong>سبب الإيقاف:</strong> {selectedUser.suspensionReason}</p>
                  )}
                  <p><strong>تاريخ الإنشاء:</strong> {formatDate(selectedUser.createdAt, true)}</p>
                </>
              ) : (
                // View Mode
                <>
                  <h6 className="mt-3 mb-2 text-primary">المعلومات الأساسية</h6>
                  <p>
                    <strong>الاسم:</strong> {selectedUser.name}
                    {selectedUser.clientCode && (
                      <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.85em' }} dir="ltr">{selectedUser.clientCode}</span>
                    )}
                  </p>
                  <p><strong>البريد الإلكتروني:</strong> {selectedUser.email}</p>
                  <p><strong>الجوال:</strong> <span style={{ direction: 'ltr', display: 'inline-block' }}>{selectedUser.phone || '-'}</span></p>
                  <p><strong>الدور:</strong> {getRoleLabel(selectedUser.role)}</p>
                  {isOwnerRole(selectedUser.role) && (
                    <p>
                      <strong>العقارات:</strong>{' '}
                      {selectedUser.propertyCount === null || selectedUser.propertyCount === undefined ? (
                        <span className="text-muted">-</span>
                      ) : selectedUser.propertyCount > 0 ? (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-decoration-none"
                          onClick={() => {
                            handleViewProperties(selectedUser._id);
                            setShowDetailsModal(false);
                          }}
                        >
                          عرض العقارات ({selectedUser.propertyCount})
                        </Button>
                      ) : (
                        <span className="text-muted">لا توجد عقارات</span>
                      )}
                    </p>
                  )}
                  <p><strong>الحالة:</strong> {getStatusBadge(selectedUser)}</p>
                  {selectedUser.suspensionReason && (
                    <p><strong>سبب الإيقاف:</strong> {selectedUser.suspensionReason}</p>
                  )}

                  <h6 className="mt-4 mb-2 text-primary">تاريخ الحساب</h6>
                  <p><strong>تاريخ الإنشاء:</strong> {formatDate(selectedUser.createdAt, true)}</p>

                  {selectedUser.invitedAt && (
                    <p><strong>تاريخ الدعوة:</strong> {formatDate(selectedUser.invitedAt, true)}</p>
                  )}

                  {selectedUser.activatedAt && (
                    <p><strong>تاريخ التفعيل:</strong> {formatDate(selectedUser.activatedAt, true)}</p>
                  )}

                  {selectedUser.status === 'invited' && selectedUser.activationExpires && (
                    <p>
                      <strong>تنتهي صلاحية الدعوة:</strong>{' '}
                      {formatDate(selectedUser.activationExpires, true)}
                      {(() => {
                        const invStatus = getInvitationStatus(selectedUser);
                        if (invStatus?.type === 'expired') {
                          return <Badge bg="danger" className="ms-2">منتهية</Badge>;
                        } else if (invStatus?.type === 'expiring') {
                          return <Badge bg="warning" text="dark" className="ms-2">⚠️ {invStatus.daysLeft} يوم متبقي</Badge>;
                        }
                        return null;
                      })()}
                    </p>
                  )}

                  {selectedUser.lastLogin && (
                    <p><strong>آخر دخول:</strong> {formatDate(selectedUser.lastLogin, true)}</p>
                  )}

                  {selectedUser.invitedBy && (
                    <p><strong>مدعو بواسطة:</strong> {selectedUser.invitedBy.name || selectedUser.invitedBy}</p>
                  )}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isEditMode ? (
            <>
              <Button variant="secondary" onClick={handleCancelEdit}>
                إلغاء
              </Button>
              <Button variant="primary" onClick={handleUpdateUser}>
                حفظ التغييرات
              </Button>
            </>
          ) : (
            canManageUsers && (
              <Button variant="primary" onClick={handleEnterEditMode}>
                تعديل
              </Button>
            )
          )}
        </Modal.Footer>
      </Modal>

      {/* Suspend User Modal */}
      <Modal show={showSuspendModal} onHide={() => setShowSuspendModal(false)} dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title>إيقاف المستخدم</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>هل أنت متأكد من إيقاف المستخدم <strong>{selectedUser?.name}</strong>؟</p>
          <Form.Group>
            <Form.Label>سبب الإيقاف</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              required
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="أدخل سبب الإيقاف..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuspendModal(false)}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={handleSuspendUser}>
            إيقاف المستخدم
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete User Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title>حذف المستخدم</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>⚠️ تحذير:</strong> هذا الإجراء دائم ولا يمكن التراجع عنه
          </Alert>
          <p>هل أنت متأكد من حذف المستخدم <strong>{selectedUser?.name}</strong>؟</p>
          <p className="text-muted" style={{ fontSize: '0.9em' }}>
            البريد الإلكتروني: {selectedUser?.email}
          </p>
          <Alert variant="info" className="mt-3">
            <small>
              ملاحظة: لا يمكن حذف المستخدمين الذين لديهم عقارات أو حجوزات مسجلة.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={handleDeleteUser}>
            تأكيد الحذف
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </DesktopLayout>
  );
};

export default UserManagement;
